
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Conversation, Message } from "@/types";
import { 
  fetchConversationsService, 
  fetchMessagesService, 
  createConversationService,
  deleteConversationService,
  renameConversationService,
  updateConversationTemplateService,
} from "../services";
import { useRef, useCallback } from "react";

export function useConversationActions(
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>,
  setCurrentConversation: React.Dispatch<React.SetStateAction<Conversation | null>>
) {
  const { toast } = useToast();
  
  // Safe navigation hook that doesn't throw if Router context is missing
  let navigate: ReturnType<typeof useNavigate> | null = null;
  try {
    navigate = useNavigate();
  } catch (error) {
    console.warn("Router context not available for ChatProvider navigation");
  }
  const fetchInProgressRef = useRef(false);
  const creationInProgressRef = useRef(false);
  const lastFetchKeyRef = useRef<string | null>(null);
  
  const currentConversationRef = useRef<Conversation | null>(null);
  
  const trackCurrentConversation = (conversation: Conversation | null) => {
    currentConversationRef.current = conversation;
    setCurrentConversation(conversation);
  };

  const fetchConversations = useCallback(async (userId: string): Promise<void> => {
    if (!userId || fetchInProgressRef.current) {
      console.log("Fetch conversations: skipping - no user or already in progress");
      return;
    }
    
    try {
      fetchInProgressRef.current = true;
      const conversationsData = await fetchConversationsService(userId);
      setConversations(conversationsData);
      return;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      return;
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [setConversations]);

  const fetchMessages = useCallback(async (conversationId: string, userId?: string) => {
    if (!userId && !conversationId) {
      console.log("No user, skipping fetchMessages");
      return;
    }
    
    // Anti-race: criar chave única para este fetch
    const key = `${conversationId}:${Date.now()}`;
    lastFetchKeyRef.current = key;
    
    try {
      console.log("Fetching messages for conversation:", conversationId);
      const { messages: messagesData, conversation } = await fetchMessagesService(conversationId, userId || '');
      
      // Ignorar se não é mais a requisição mais recente
      if (lastFetchKeyRef.current !== key) {
        console.log("Race condition detected - ignoring outdated response");
        return;
      }
      
      if (messagesData) {
        setMessages(messagesData);
      }
      
      if (conversation) {
        trackCurrentConversation(conversation);
      } else if (conversationId) {
        console.log("Conversation not found or no messages");
        if (messagesData?.length === 0) {
          console.log("No messages found, might need new conversation");
        }
      }
    } catch (error) {
      // Ignorar erros de requisições antigas
      if (lastFetchKeyRef.current === key) {
        console.error("Error fetching messages:", error);
      }
    }
  }, [setMessages]);

  const createNewConversation = useCallback(async (userId: string, templateName: string = "lovable_gpt"): Promise<string | null> => {
    if (!userId || creationInProgressRef.current) {
      console.log("Create conversation: skipping - no user or already in progress");
      return null;
    }
    
    try {
      creationInProgressRef.current = true;
      console.log("Creating new conversation for user:", userId, "with template:", templateName);
      
      // Always create a new conversation when requested
      const newConversation = await createConversationService(userId, "Nova conversa", templateName);
      
      if (newConversation) {
        trackCurrentConversation(newConversation);
        setMessages([]);
        
        console.log("Navigating to new conversation:", newConversation.id);
        navigate?.(`/chat/${newConversation.id}`);
        
        // Update conversations list
        await fetchConversations(userId);
        
        return newConversation.id;
      }
      
      return null;
    } catch (error) {
      console.error("Error creating new conversation:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar uma nova conversa.",
        variant: "destructive",
      });
      return null;
    } finally {
      creationInProgressRef.current = false;
    }
  }, [navigate, fetchConversations, setMessages, toast]);

  const updateConversationTemplate = useCallback(async (conversationId: string, templateName: string, userId: string): Promise<boolean> => {
    try {
      const success = await updateConversationTemplateService(conversationId, templateName, userId);
      
      if (success) {
        // Atualizar conversa no estado local
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, metadata: { ...conv.metadata, template: templateName } }
              : conv
          )
        );
        
        // Atualizar conversa atual se for a mesma
        if (currentConversationRef.current?.id === conversationId) {
          const updatedConv = {
            ...currentConversationRef.current,
            metadata: { ...currentConversationRef.current.metadata, template: templateName }
          };
          trackCurrentConversation(updatedConv);
        }
        
        toast({
          title: "Estilo atualizado",
          description: "O estilo da conversa foi alterado com sucesso.",
        });
      }
      
      return success;
    } catch (error) {
      console.error("Error updating conversation template:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o estilo da conversa.",
        variant: "destructive",
      });
      return false;
    }
  }, [setConversations, toast]);

  const deleteConversation = useCallback(async (conversationId: string, userId: string) => {
    if (!userId) return;
    
    const success = await deleteConversationService(conversationId, userId);
    
    if (success) {
      setConversations(prevConversations => {
        const updatedConversations = prevConversations.filter(conv => conv.id !== conversationId);
        
        if (currentConversationRef.current?.id === conversationId) {
          if (updatedConversations.length > 0) {
            const firstConversation = updatedConversations[0];
            navigate?.(`/chat/${firstConversation.id}`, { replace: true });
          } else {
            // Limpar estado atual e navegar para tela inicial
            setMessages([]);
            trackCurrentConversation(null);
            navigate?.('/chat', { replace: true });
          }
        }
        
        return updatedConversations;
      });
    }
  }, [setConversations, navigate, setMessages]);

  const renameConversation = useCallback(async (conversationId: string, newTitle: string, userId: string) => {
    if (!userId || !newTitle.trim()) return;
    
    const success = await renameConversationService(conversationId, newTitle, userId);
    
    if (success) {
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === conversationId 
            ? { ...conv, title: newTitle.trim() } 
            : conv
        )
      );
      
      if (currentConversationRef.current?.id === conversationId) {
        trackCurrentConversation(
          currentConversationRef.current ? { ...currentConversationRef.current, title: newTitle.trim() } : null
        );
      }
    }
  }, [setConversations]);

  return {
    fetchConversations,
    fetchMessages,
    createNewConversation,
    deleteConversation,
    renameConversation,
    updateConversationTemplate,
  };
}
